/**
 * @file index.tsx
 *
 * @fileoverview Shows the job preferences widget when in view mode.
 */

import { Map } from '~/jobiqo/components/Map';
import { MapMarkerProps } from '~/jobiqo/components/Map/Marker';
import { AddLink } from '~/jobiqo/components/Organization/Widgets/AddLink';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { ORG_INFO_WIDGET_KEY } from '~/jobiqo/components/Organization/Widgets/InfoBox';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { OfficeIcon } from '~/vcl/icons/Office';

export interface OrganizationLocationsWidgetViewProps {
  /**
   * The current resume.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string | undefined;
}

export const OrganizationLocationsWidgetView = ({
  organization,
  boxId,
}: OrganizationLocationsWidgetViewProps) => {
  const markers: MapMarkerProps[] = [];
  const { furtherLocations, organizationLocation } = organization;

  // Add main location
  if (organizationLocation?.lat && organizationLocation?.lon) {
    markers.push({
      id: organizationLocation.address ?? '',
      text: organizationLocation.address ? (
        <FlexItems flexWrap="nowrap">
          <FlexItem marginRight={4}>
            <OfficeIcon color="primary" title="Location" />
          </FlexItem>
          <FlexItem>
            <p>{organizationLocation.address}</p>
          </FlexItem>
        </FlexItems>
      ) : (
        ''
      ),
      lat: organizationLocation.lat as number,
      long: organizationLocation.lon as number,
      editLink: (
        <EditLink organization={organization} boxId={ORG_INFO_WIDGET_KEY} />
      ),
      prominent: true,
    });
  }

  // Add further locations
  if (furtherLocations && furtherLocations.length > 0) {
    markers.push(
      ...furtherLocations
        .filter(
          (furtherAdd) => furtherAdd?.address?.lat && furtherAdd?.address?.lon,
        )
        .map((furtherAddress, index) => {
          // cast because we already ensured lat long are here in the filter.
          return {
            id: furtherAddress?.name ? furtherAddress?.name : undefined,
            text: furtherAddress && (
              <FlexItems flexWrap="nowrap">
                <FlexItem marginRight={4}>
                  <OfficeIcon color="primary" title="Location" />
                </FlexItem>
                <FlexItem>
                  <p>{furtherAddress.name}</p>
                  <p>
                    {`${furtherAddress.address?.addressLine1}, ${furtherAddress.address?.locality}`}
                  </p>
                </FlexItem>
              </FlexItems>
            ),
            lat: furtherAddress?.address?.lat as number,
            long: furtherAddress?.address?.lon as number,
            editLink: (
              <EditLink
                organization={organization}
                boxId={boxId}
                fieldIndex={index}
              />
            ),
          };
        }),
    );
  }

  return (
    <FlexItems justifyContent="flex-end" gap={6}>
      <AddLink organization={organization} boxId={boxId} />
      {markers.length > 0 && <Map markers={markers} />}
    </FlexItems>
  );
};
export default OrganizationLocationsWidgetView;
